/** A row of content that is designed to appear within a larger layout,
 *  often representing a larger set of content like a stream, a search,
 *  or a curated collection.
 */
import React, { useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiNetworkbarStrip } from '@wix/da-papi-types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import StripWrapper from './StripWrapper';
import TopicsStrip from './TopicsStrip';
import DeviationsStrip from './DeviationsStrip';
import WatchSuggestionStrip from './WatchSuggestionStrip';
import MeasuredStripSlider from './MeasuredStripSlider';
import StripSlider from './StripSlider';
import TagsStrip from './TagsStrip';
import {
  getStripDeviationType,
  isCollectionStrip,
  isDeviationStrip,
  isGroupStrip,
  isTagStrip,
  isTopNecoStrip,
  isUserStrip,
  isWatchSuggestionStrip,
} from '../../helpers/streams';

export interface Props {
  /** stream data and initial array of content and url pointing to larger collection */
  strip: PapiNetworkbarStrip;
  /** is this strip displayed at the very top of the home page */
  isTopStrip?: boolean;
  isSearchPage?: boolean;

  className?: string;
  sliderClassName?: string;
  sliderContentClassName?: string;
}

const Strip: React.FC<Props> = props => {
  const isMobile = useContext(MobileContext);

  const {
    strip,
    sliderClassName,
    sliderContentClassName,
    className,
    isSearchPage,
  } = props;

  if (isWatchSuggestionStrip(strip)) {
    return <WatchSuggestionStrip strip={strip} />;
  }

  if (isTopNecoStrip(strip)) {
    return (
      <TopicsStrip
        strip={strip}
        isSearchPage={isSearchPage}
        className={className}
      />
    );
  }

  if (isDeviationStrip(strip)) {
    const shouldUseMosaic =
      isMobile &&
      !isSearchPage &&
      getStripDeviationType(strip) !== DeviationTypes.TIER;
    return (
      <DeviationsStrip
        strip={strip}
        disableShowLess={!isSearchPage}
        className={className}
        sliderClassName={sliderClassName}
        sliderContentClassName={sliderContentClassName}
        useMosaic={shouldUseMosaic}
        isSearchPage={isSearchPage}
      />
    );
  }

  if (isTagStrip(strip)) {
    return (
      <TagsStrip
        strip={strip}
        isSearchPage={isSearchPage}
        className={className}
      />
    );
  }

  if (
    !isCollectionStrip(strip) &&
    !isGroupStrip(strip) &&
    !isUserStrip(strip)
  ) {
    return null;
  }

  return (
    <StripWrapper {...props}>
      {isSearchPage ? (
        <MeasuredStripSlider
          strip={strip}
          disableShowLess={!isSearchPage}
          className={sliderClassName}
          contentClassName={sliderContentClassName}
        />
      ) : (
        <StripSlider strip={strip} disableShowLess={!isSearchPage} />
      )}
    </StripWrapper>
  );
};
Strip.displayName = 'Strip';

export default Strip;
